import { Address, ViaCEPResponse } from '../../../shared/models/address.model';
import { Component, effect, input, model, output } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { sanitizeFields, validateBlank, watchFormRecursively } from '../../../shared/utils/form-utils';

import { AddressService } from '../../../services/address/address.service';
import { ButtonModule } from 'primeng/button';
import { FloatLabelModule } from 'primeng/floatlabel';
import { FormFieldFeedbackPipe } from '../../../pipes/form-field-feedback/form-field-feedback.pipe';
import { InputMaskModule } from 'primeng/inputmask';
import { InputText } from 'primeng/inputtext';
import { MessageService } from 'primeng/api';
import { TooltipModule } from 'primeng/tooltip';
import { finalize } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

@Component({
  selector: 'app-address-form',
  imports: [
    ButtonModule,
    InputText,
    InputMaskModule,
    FormFieldFeedbackPipe,
    TooltipModule,
    ReactiveFormsModule,
    FloatLabelModule
  ],
  templateUrl: './address-form.component.html',
  styleUrl: './address-form.component.scss'
})
export class AddressFormComponent {
  readonly addressForm = new FormGroup({
    street: new FormControl<string>('', {
      validators: [
        Validators.required,
        validateBlank()
      ]
    }),
    number: new FormControl<string>('', {
      validators: [
        Validators.required,
        validateBlank()
      ]
    }),
    neighborhood: new FormControl<string>('', {
      validators: [
        Validators.required,
        validateBlank()
      ]
    }),
    zipCode: new FormControl<string>('', {
      validators: [
        Validators.required,
        validateBlank()
      ]
    }),
    complement: new FormControl<string>(''),
    city: new FormControl<string>('', {
      validators: [
        Validators.required,
        validateBlank()
      ]
    }),
    state: new FormControl<string>('', {
      validators: [
        Validators.required,
        validateBlank()
      ]
    })
  });

  readonly initialData = input<Address | null>(null);
  readonly onSubmit = output<any>();
  readonly enabled = model<boolean>(true);
  readonly isLoading = model.required<boolean>();

  readonly id = uuidv4();

  readonly formWatched = watchFormRecursively(this.addressForm);

  constructor(private readonly messageService: MessageService,
    private readonly addressService: AddressService) {
    effect(() => {
      if (this.enabled()) {
        this.addressForm.enable();
      } else {
        this.addressForm.disable();
      }
    });

    effect(() => {
      if (this.initialData()) {
        this.enabled.set(false);
        this.addressForm.patchValue(this.initialData()!);
      } else {
        this.enabled.set(true);
        this.addressForm.reset();
      }
    });
  }

  onFetchAndFillFromZipCode(): void {
    const zipCode = this.addressForm.get('zipCode')?.value;
    if (!zipCode) {
      return;
    }

    this.isLoading.set(true);
    this.addressForm.disable();

    this.addressService.fetchAddressByZipCode(zipCode)
      .pipe(finalize(() => {
        this.isLoading.set(false);
        this.addressForm.enable();
      }))
      .subscribe({
        next: (addressData: ViaCEPResponse) => {
          if (addressData.erro) {
            this.messageService.add({
              severity: 'warn',
              summary: 'Atenção',
              detail: 'CEP não encontrado! Verifique o CEP informado.',
              life: 4000
            });
            return;
          }

          this.addressForm.patchValue({
            street: addressData.logradouro,
            neighborhood: addressData.bairro,
            city: addressData.localidade,
            state: addressData.uf,
            complement: addressData.complemento
          });
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Erro',
            detail: 'Falha ao recuperar o endereço! CEP inválido ou serviço indisponível.',
            life: 4000
          });
        }
      });
  }

  submitForm(): void {
    this.isLoading.set(true);

    if (this.addressForm.invalid) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Atenção',
        detail: 'Preencha todos os campos corretamente!',
        life: 4000
      });

      this.isLoading.set(false);
      return;
    }

    sanitizeFields(this.addressForm);
    this.onSubmit.emit(this.addressForm.value);
  }
}
